import { useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { BUILDER_JSON } from '~/shared/constants/queryCacheKeys';
import useLoadState from '~/shared/utils/useLoadState';
import { useSession } from '~/techstyle-shared/redux-core';

import { getAllBuilderJson } from './getAllBuilderJson';
import { getBuilderJson } from './getBuilderJson';
import { getBuilderJsonCacheKey } from './getBuilderJsonCacheKey';
import { isBuilderPreview } from './isBuilderPreview';
import useBuilderTargeting from './useBuilderTargeting';

const DEFAULT_BUILDER_QUERIES = { isPreview: false };
const DEFAULT_QUERY = {};
const DEFAULT_OPTIONS = {};
const DEFAULT_ADDITIONAL_QUERY_PARAMS = {};
// Cache Builder responses for 5 minutes so back-navigation skips re-fetching
const BUILDER_STALE_TIME = 5 * 60 * 1000;

export default function useBuilderJson({
  modelName,
  urlPath,
  query = DEFAULT_QUERY,
  builderQueries = DEFAULT_BUILDER_QUERIES,
  ssrBuilderJson,
  options = DEFAULT_OPTIONS,
  additionalQueryParams = DEFAULT_ADDITIONAL_QUERY_PARAMS,
}) {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useSession();
  const { loggedInLoaded } = useLoadState();
  const lowerCaseUrlPath = urlPath?.toLowerCase();

  const pageTargetingOptions = useBuilderTargeting({
    isPreview: builderQueries?.isPreview,
  });

  const cacheKey = useMemo(
    () =>
      getBuilderJsonCacheKey({
        pageTargetingOptions,
        query,
        urlPath: lowerCaseUrlPath,
        modelName,
        additionalQueryParams,
      }),
    [
      additionalQueryParams,
      lowerCaseUrlPath,
      modelName,
      pageTargetingOptions,
      query,
    ]
  );

  // Seed the React Query cache with SSR data before useQuery runs. This prevents
  // a duplicate fetch when the client cacheKey doesn't exactly match ssrBuilderJsonKey
  // (e.g. due to transient object references in pageTargetingOptions that affect the
  // hash but not the actual Builder userAttributes). SSR already fetches with full
  // targeting so the data is correct and fresh.
  if (ssrBuilderJson && !queryClient.getQueryData([BUILDER_JSON, cacheKey])) {
    queryClient.setQueryData([BUILDER_JSON, cacheKey], ssrBuilderJson);
  }

  // models with skipUserAttributes don't need user state to settle, so skip the gate for them.
  // In preview/editing mode, never gate the fetch — the editor needs BuilderComponent to mount
  // immediately for the postMessage handshake to succeed within the 10s timeout.
  const isUserStateInTransition =
    !isBuilderPreview() &&
    !options?.skipUserAttributes &&
    isLoggedIn &&
    !loggedInLoaded;

  const { data: builderJson, isLoading } = useQuery({
    queryKey: [BUILDER_JSON, cacheKey],
    queryFn: async () => {
      const fetcher = options?.getAll ? getAllBuilderJson : getBuilderJson;
      const { builderJson: result } = await fetcher({
        modelName,
        urlPath: lowerCaseUrlPath,
        query,
        pageTargetingOptions,
        additionalQueryParams,
        skipUserAttributes: options.skipUserAttributes || false,
      });
      return result;
    },
    enabled: !isUserStateInTransition,
    staleTime: BUILDER_STALE_TIME,
  });

  return { builderJson, isLoading };
}
